import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import { lies, schick } from "../../api/client";
import type { Begehung, BegehungArt, BegehungKurz, Leistung, Projekt } from "../../api/types";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { BEGEHUNG_ART, BEGEHUNG_GRUNDLAGE, ERGEBNIS, datum, heute } from "../../lib/format";
import { useLeistungen } from "./MaengelReiter";
import { Auswahl } from "../../components/Auswahl";

export function BegehungenReiter({ projekt, kann }: { projekt: Projekt; kann: boolean }) {
  const liste = useQuery({
    queryKey: ["begehungen", projekt.id],
    queryFn: () => lies<BegehungKurz[]>(`/projekte/${projekt.id}/begehungen`),
  });
  const [neu, setNeu] = useState(false);
  return (
    <section className="karte">
      <div className="karte-kopf">
        <h2>Abnahmen, Zustandsfeststellungen, Begehungen</h2>
        <div className="luft" />
        {kann && <button className="knopf haupt" onClick={() => setNeu(true)}><Plus size={16} weight="bold" /> Termin vor Ort</button>}
      </div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((g) => (
          <Link key={g.id} to={`/g/${g.id}`} className="zeile">
            <span className="nr ziffern">{datum(g.datum)}</span>
            <div className="haupt">
              <div>{BEGEHUNG_ART[g.art]}{g.leistung && ` · ${g.leistung}`}</div>
              <div className="mute">{BEGEHUNG_GRUNDLAGE[g.art]}{g.ort && ` · ${g.ort}`} · {g.maengel} Mängel</div>
            </div>
            <span style={{ display: "flex", gap: 6 }}>
              {g.ergebnis !== "offen" && <span className={`chip ${g.ergebnis === "verweigert" ? "fehler" : "gut"}`}>{ERGEBNIS[g.ergebnis]}</span>}
              {g.status === "entwurf" && <span className="chip warn">Entwurf</span>}
            </span>
          </Link>
        ))}
        {liste.data?.length === 0 && <div className="leer">Noch kein Termin vor Ort.</div>}
      </div>
      <NeueBegehung projekt={projekt} offen={neu} zu={() => setNeu(false)} />
    </section>
  );
}

/** Mehrere Leistungen in einem Termin abnehmen (Wanderung 022): Häkchen statt Auswahl.
 *  Die Reihenfolge der Häkchen ist die Reihenfolge in der Niederschrift. */
export function LeistungenWahl({ leistungen, gewaehlt, setze }: {
  leistungen: Leistung[]; gewaehlt: string[]; setze: (ids: string[]) => void;
}) {
  return (
    <div className="leistungen-wahl">
      {leistungen.map((l) => (
        <label key={l.id} className="haken">
          <input type="checkbox" checked={gewaehlt.includes(l.id)}
                 onChange={(e) => setze(e.target.checked ? [...gewaehlt, l.id] : gewaehlt.filter((x) => x !== l.id))} />
          <span>{l.bezeichnung}{l.an && <span className="mute"> – {l.an}</span>}
            {l.abnahme_am && <span className="mute"> · abgenommen {datum(l.abnahme_am)}</span>}</span>
        </label>
      ))}
    </div>
  );
}

function NeueBegehung({ projekt, offen, zu }: { projekt: Projekt; offen: boolean; zu: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const leistungen = useLeistungen(projekt.id);
  const [f, setF] = useState<{ art: BegehungArt; leistung_ids: string[]; datum: string; ort: string }>(
    { art: "abnahme", leistung_ids: [], datum: heute(), ort: "" });
  const anlegen = useMutation({
    mutationFn: () => schick<Begehung>(`/projekte/${projekt.id}/begehungen`, f),
    onSuccess: (g) => {
      qc.invalidateQueries({ queryKey: ["begehungen", projekt.id] });
      zu();
      nav(`/g/${g.id}`);
    },
  });
  const brauchtLeistung = f.art === "abnahme" || f.art === "teilabnahme";
  return (
    <Dialog offen={offen} zu={zu} titel="Termin vor Ort" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={anlegen.isPending || (brauchtLeistung && !f.leistung_ids.length)}
              onClick={() => anlegen.mutate()}>Anlegen</button>
    </>}>
      <div className="raster">
        <Feld titel="Art" breit>
          <Auswahl value={f.art} onChange={(e) => setF({ ...f, art: e.target.value as BegehungArt })}>
            {Object.entries(BEGEHUNG_ART).map(([k, v]) => (
              <option key={k} value={k}>{v}{BEGEHUNG_GRUNDLAGE[k] && ` (${BEGEHUNG_GRUNDLAGE[k]})`}</option>
            ))}
          </Auswahl>
        </Feld>
        {/* Kein <Feld>: das ist ein <label>, und Häkchen in einem label schalten das erste mit. */}
        {!!leistungen.data?.length && (
          <div className="feld breit">
            <span>{brauchtLeistung ? "Leistungen (mindestens eine)" : "Leistungen"}</span>
            <LeistungenWahl leistungen={leistungen.data} gewaehlt={f.leistung_ids}
                            setze={(ids) => setF({ ...f, leistung_ids: ids })} />
          </div>
        )}
        <Feld titel="Datum"><input type="date" className="eingabe" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} /></Feld>
        <Feld titel="Ort"><input className="eingabe" value={f.ort} onChange={(e) => setF({ ...f, ort: e.target.value })} /></Feld>
      </div>
      {leistungen.data?.length === 0 && brauchtLeistung && (
        <p className="mute">Für eine Abnahme braucht es zuerst eine Leistung (Reiter „Leistungen“).</p>
      )}
      <Meldung fehler={anlegen.error} />
    </Dialog>
  );
}
